import {
  GraphQLFieldConfig,
  GraphQLNonNull,
  GraphQLFieldConfigArgumentMap
} from 'graphql'
import { GenericMutationResponseType } from '../types/generic-mutation-response'
import { Context } from '../shared'
import { TaggingArgs, taggingArgs } from '../types/tagging-args'
import {
  tagApplicationsByFiltersArg,
  tagServersByFiltersArg,
  TagFilterMutationArgs,
  TagFilterMutationInputArgs,
  TagApplicationsByFiltersInputArgs,
  TagServersByFiltersInputArgs,
  TagVulnerabilitiesByFiltersInputArgs,
  tagVulnerabilitiesByFiltersArg,
  tagLibrariesByFiltersArg,
  TagLibrariesByFiltersInputArgs
} from '../types/tag-filter-mutation-args'
import {
  ContrastQueryParameters,
  mapApplicationFilters,
  mapServerFilters,
  mapVulnerabilityFilters,
  mapLibraryFilters
} from '../endpoint-helpers'

export type BulkTagMutationConfig = GraphQLFieldConfig<
  any,
  Context,
  TaggingArgs
>

function getBulkTagsMutation({
  description,
  endpointPath,
  idsKey
}): BulkTagMutationConfig {
  return {
    description,
    type: new GraphQLNonNull(GenericMutationResponseType),
    args: taggingArgs,
    async resolve(_source, args, context) {
      const { input } = args
      if (!input) {
        throw new Error('mutation input parameter is required')
      }

      const { add, remove, ids, organizationID } = input
      const { connector } = context

      const params = new ContrastQueryParameters()
      params.append('expand', 'skip_links')

      await connector.put(
        `/${organizationID}/tags/${endpointPath}/bulk?${params}`,
        {
          [idsKey]: ids,
          tags: add,
          tags_remove: remove
        }
      )

      return { _placeholder: true }
    }
  }
}

export const modifyApplicationTags = getBulkTagsMutation({
  description: 'Add and remove tags from one or more applications.',
  idsKey: 'applications_id',
  endpointPath: 'applications'
})

export const modifyLibraryTags = getBulkTagsMutation({
  description: 'Add and remove tags from one or more library',
  idsKey: 'library_hashes',
  endpointPath: 'libraries'
})

export const modifyServersTags = getBulkTagsMutation({
  description: 'Add and remove tags from one or more servers.',
  idsKey: 'servers_id',
  endpointPath: 'servers'
})

export const modifyVulnerabilitiesTags = getBulkTagsMutation({
  description: 'Add and remove tags from one or more vulnerabilities.',
  idsKey: 'traces_uuid',
  endpointPath: 'traces'
})

interface BulkFiltersTagMutationArgs<TInputArgs> {
  readonly description: string
  readonly args: GraphQLFieldConfigArgumentMap
  readonly endpointPath: string
  readonly mapArgsToFilters: (filters: TagFilterMutationArgs<TInputArgs>) => any
}

export type BulkTagByFiltersMutationConfig<
  TInputArgs extends TagFilterMutationInputArgs<object>
> = GraphQLFieldConfig<any, Context, TagFilterMutationArgs<TInputArgs>>

function getBulkFiltersTagsMutation<
  TInputArgs extends TagFilterMutationInputArgs<object>
>({
  description,
  args,
  endpointPath,
  mapArgsToFilters
}: BulkFiltersTagMutationArgs<TInputArgs>): BulkTagByFiltersMutationConfig<TInputArgs> {
  return {
    description,
    type: new GraphQLNonNull(GenericMutationResponseType),
    args,
    async resolve(_source, args, context) {
      const { input } = args

      if (!input) {
        throw new Error('mutation input parameter is required')
      }

      const {
        input: { organizationID, add = [], remove = [] }
      } = args

      const { connector } = context

      if (add.length + remove.length === 0) {
        throw new Error('there must be at least one tag to remove or add')
      }

      await connector.put(`/${organizationID}/${endpointPath}/bulk/tag`, {
        filters: mapArgsToFilters(args),
        addTags: add,
        removeTags: remove
      })

      return { _placeholder: true }
    }
  }
}

export const modifyApplicationTagsByFilters =
  getBulkFiltersTagsMutation<TagApplicationsByFiltersInputArgs>({
    description:
      'Add and remove tags on a collection of applications specified by filter',
    args: tagApplicationsByFiltersArg,
    endpointPath: 'applications',
    mapArgsToFilters: ({ input: { filters, excludedApplications } }) => ({
      ...mapApplicationFilters(filters),
      excludedApplications
    })
  })

export const modifyServerTagsByFilters =
  getBulkFiltersTagsMutation<TagServersByFiltersInputArgs>({
    description:
      'Add and remove tags on a collection of servers specified by filter',
    args: tagServersByFiltersArg,
    endpointPath: 'servers',
    mapArgsToFilters: ({ input: { filters, excludedServers } }) => ({
      ...mapServerFilters(filters),
      excludedServers
    })
  })

export const modifyVulnerabilitiesTagsByFilters =
  getBulkFiltersTagsMutation<TagVulnerabilitiesByFiltersInputArgs>({
    description:
      'Add and remove tags on a collection of vulnerabililties specified by filter',
    args: tagVulnerabilitiesByFiltersArg,
    endpointPath: 'orgtraces',
    mapArgsToFilters: ({ input: { filters, excludedVulnerabilities } }) => ({
      ...mapVulnerabilityFilters(filters),
      excludedTraces: excludedVulnerabilities
    })
  })

export const modifyLibrariesTagsByFilters =
  getBulkFiltersTagsMutation<TagLibrariesByFiltersInputArgs>({
    description:
      'Add and remove tags on a collection of libraries specified by filter',
    args: tagLibrariesByFiltersArg,
    endpointPath: 'libraries',
    mapArgsToFilters: ({ input: { filters, excludedHashes } }) => ({
      ...mapLibraryFilters(filters),
      excludedHashes
    })
  })
